import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import BobineLogo from '../components/BobineLogo';
import ServicesDropdown from '../components/ServicesDropdown';
import MobileNav from '../components/MobileNav';

const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;
const serif = "'Georgia', 'Times New Roman', serif";

const PricingPage = ({ user }) => {
  const navigate = useNavigate();
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [userPlan, setUserPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [libertePacks, setLibertePacks] = useState([]);
  const [livres, setLivres] = useState([]);
  const [options, setOptions] = useState({});
  const [discount, setDiscount] = useState(20);
  const [billingAnnual, setBillingAnnual] = useState(false);
  const [annualPrice, setAnnualPrice] = useState(48);

  useEffect(() => {
    // Fetch dynamic pricing
    axios.get(`${API}/pricing`).then(res => {
      setPlans(res.data.plans || []);
      setLibertePacks(res.data.liberte_packs || []);
      setLivres(res.data.products || []);
      setOptions(res.data.options || {});
      setDiscount(res.data.heritage_discount || 20);
      setAnnualPrice(res.data.heritage_annual_price || 48);
    }).catch(() => {});

    // Check user plan
    const token = localStorage.getItem('bobine_token');
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          const plan = res.data?.user?.plan;
          setUserPlan(plan);
          setIsSubscriber(plan === 'heritage');
        }).catch(() => {});
    }
  }, []);

  const handleCheckout = async (planId, productId) => {
    const token = localStorage.getItem('bobine_token');
    if (!token) { navigate('/compte?redirect=/offres'); return; }
    if (planId === 'eveil') { navigate('/dashboard'); return; }
    setLoading(true);
    try {
      const body = planId
        ? { plan_id: planId, billing: planId === 'heritage' && billingAnnual ? 'annual' : 'monthly', origin_url: window.location.origin }
        : { product_id: productId, origin_url: window.location.origin };
      const res = await axios.post(`${API}/checkout/create-session`, body, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const PlanCard = ({ plan, isCurrent, popular }) => {
    const isHeritage = plan.plan_id === 'heritage';
    const showAnnual = isHeritage && billingAnnual;
    const displayPrice = showAnnual ? annualPrice : plan.price;
    const displayPeriod = showAnnual ? 'an' : plan.billing_period;
    const monthlyEquiv = showAnnual ? (annualPrice / 12) : null;

    return (
      <div data-testid={`sub-card-${plan.plan_id}`} style={{
        border: popular ? '2px solid #D4AF37' : '1px solid rgba(212,175,55,0.2)',
        borderRadius: 14, overflow: 'hidden',
        backgroundColor: popular ? 'rgba(212,175,55,0.04)' : 'rgba(212,175,55,0.01)',
      }}>
        {popular && (
          <div style={{ backgroundColor: 'rgba(212,175,55,0.08)', color: '#000', textAlign: 'center', padding: '6px 0', fontFamily: serif, fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>
            {showAnnual ? 'Economisez 20%' : 'Le plus demande'}
          </div>
        )}
        <div style={{ padding: '24px 22px' }}>
          <h3 style={{ fontFamily: serif, fontSize: 20, color: '#D4AF37', marginBottom: 4, fontWeight: 400 }}>{plan.name}</h3>
          <div style={{ marginBottom: 16, marginTop: 12 }}>
            {plan.price === 0 ? (
              <span style={{ fontFamily: serif, fontSize: 28, color: '#D4AF37', fontWeight: 500 }}>Gratuit</span>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: serif, fontSize: 28, color: '#D4AF37', fontWeight: 500 }}>{displayPrice.toFixed(2).replace('.', ',')} EUR</span>
                  <span style={{ fontFamily: serif, fontSize: 13, color: '#FAF3E0' }}> /{displayPeriod}</span>
                  {/* Badge "1er mois offert" avec Sceau Bobine */}
                  {plan.trial_period && (
                    <div data-testid="trial-badge" style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '4px 12px', borderRadius: 20,
                      background: 'linear-gradient(135deg, rgba(212,175,55,0.12), rgba(212,175,55,0.04))',
                      border: '1px solid rgba(212,175,55,0.25)',
                      boxShadow: '0 2px 8px rgba(212,175,55,0.1)',
                    }}>
                      <img src="/bobine-reel-transparent.png" alt="" style={{ width: 14, height: 14, objectFit: 'contain', filter: 'drop-shadow(0 0 2px rgba(212,175,55,0.4))' }} />
                      <span style={{ fontFamily: serif, fontSize: 10, color: '#D4AF37', fontWeight: 600, letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
                        1er mois offert
                      </span>
                    </div>
                  )}
                </div>
                {monthlyEquiv && (
                  <p style={{ fontFamily: serif, fontSize: 12, color: '#D4AF37', marginTop: 4, fontStyle: 'italic' }}>
                    soit {monthlyEquiv.toFixed(2).replace('.', ',')} EUR/mois
                  </p>
                )}
              </>
            )}
          </div>
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px' }}>
            {plan.features.map((f, i) => {
              const isCercle = f.toLowerCase().includes('cercle familial');
              return (
                <li key={i} style={{
                  fontFamily: serif, fontSize: isCercle ? 13 : 12,
                  color: isCercle ? '#D4AF37' : '#FAF3E0',
                  padding: isCercle ? '8px 10px' : '5px 0',
                  display: 'flex', gap: 8,
                  fontWeight: isCercle ? 600 : 400,
                  backgroundColor: isCercle ? 'rgba(212,175,55,0.06)' : 'transparent',
                  borderRadius: isCercle ? 6 : 0,
                  border: isCercle ? '1px solid rgba(212,175,55,0.15)' : 'none',
                  marginBottom: isCercle ? 6 : 0,
                }}>
                  <span style={{ color: '#D4AF37', fontSize: isCercle ? 13 : 11, flexShrink: 0 }}>
                    {isCercle ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" style={{ verticalAlign: 'middle' }}>
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                    ) : '\u2713'}
                  </span>
                  {f}
                </li>
              );
            })}
          </ul>
          <button onClick={() => handleCheckout(plan.plan_id)} disabled={loading || isCurrent} data-testid={`sub-cta-${plan.plan_id}`}
            style={{
              width: '100%', padding: '12px 0', borderRadius: 8, fontFamily: serif, fontSize: 14, fontWeight: 600, cursor: isCurrent ? 'default' : 'pointer', transition: 'all 0.3s',
              backgroundColor: isCurrent ? 'rgba(212,175,55,0.15)' : (plan.price === 0 ? 'transparent' : '#D4AF37'),
              color: isCurrent ? '#D4AF37' : (plan.price === 0 ? '#D4AF37' : '#000'),
              border: plan.price === 0 ? '1px solid rgba(212,175,55,0.3)' : 'none',
              boxShadow: plan.price > 0 && !isCurrent ? '0 2px 12px rgba(212,175,55,0.2)' : 'none',
            }}>
            {isCurrent ? 'Offre actuelle' : plan.button_text}
          </button>
        </div>
      </div>
    );
  };

  const LiberteCard = ({ pack }) => (
    <div data-testid={`liberte-card-${pack.pack_id}`} style={{
      border: '1px solid rgba(212,175,55,0.2)', borderRadius: 14, overflow: 'hidden',
      backgroundColor: 'rgba(212,175,55,0.01)',
    }}>
      <div style={{ backgroundColor: 'rgba(212,175,55,0.06)', textAlign: 'center', padding: '6px 0', fontFamily: serif, fontSize: 10, letterSpacing: 1, color: '#D4AF37', textTransform: 'uppercase' }}>
        Achat unique
      </div>
      <div style={{ padding: '24px 22px' }}>
        <h3 style={{ fontFamily: serif, fontSize: 20, color: '#D4AF37', marginBottom: 4, fontWeight: 400 }}>{pack.name}</h3>
        <p style={{ fontFamily: serif, fontSize: 12, color: '#FAF3E0', fontStyle: 'italic', marginBottom: 16 }}>{pack.description}</p>
        <div style={{ marginBottom: 16 }}>
          <span style={{ fontFamily: serif, fontSize: 28, color: '#D4AF37', fontWeight: 500 }}>{pack.price.toFixed(2).replace('.', ',')} EUR</span>
          <span style={{ fontFamily: serif, fontSize: 13, color: '#FAF3E0' }}> unique</span>
        </div>
        <div style={{ padding: '10px 0', borderTop: '1px solid rgba(212,175,55,0.1)', borderBottom: '1px solid rgba(212,175,55,0.1)', textAlign: 'center', marginBottom: 16 }}>
          <span style={{ fontFamily: serif, fontSize: 22, color: '#D4AF37', fontWeight: 500 }}>{pack.photos}</span>
          <span style={{ fontFamily: serif, fontSize: 11, color: '#FAF3E0', marginLeft: 6 }}>Tresors</span>
        </div>
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px' }}>
          {pack.features.map((f, i) => (
            <li key={i} style={{ fontFamily: serif, fontSize: 12, color: '#FAF3E0', padding: '5px 0', display: 'flex', gap: 8 }}>
              <span style={{ color: '#D4AF37', fontSize: 11, flexShrink: 0 }}>&#10003;</span>{f}
            </li>
          ))}
        </ul>
        <button onClick={() => handleCheckout(null, pack.pack_id)} disabled={loading} data-testid={`liberte-cta-${pack.pack_id}`}
          style={{
            width: '100%', padding: '12px 0', borderRadius: 8, fontFamily: serif, fontSize: 14, fontWeight: 600,
            background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none', cursor: 'pointer',
            boxShadow: '0 2px 12px rgba(212,175,55,0.2)', transition: 'all 0.3s',
          }}>
          Choisir ce pack
        </button>
      </div>
    </div>
  );

  const isLoggedIn = !!localStorage.getItem('bobine_token');

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#000' }}>
      {/* Header identique aux autres pages */}
      <header style={{ borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <BobineLogo size={22} />
          <div className="hidden md:flex items-center gap-3">
            <ServicesDropdown />
            <button onClick={() => navigate(isLoggedIn ? '/dashboard' : '/compte')} data-testid="pricing-cta"
              style={{ fontFamily: serif, fontSize: 12, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none', fontWeight: 600 }}>
              {isLoggedIn ? 'Mon Coffre-fort' : 'Commencer'}
            </button>
          </div>
          <MobileNav items={[
            { label: 'Retour', action: () => navigate('/') },
            { label: 'Accueil', action: () => navigate('/') },
            { label: 'Services', action: () => navigate('/services') },
            { label: isLoggedIn ? 'Mon Coffre-fort' : 'Commencer', action: () => navigate(isLoggedIn ? '/dashboard' : '/compte') },
          ]} />
        </div>
      </header>

      <main className="container mx-auto px-6 py-12 max-w-5xl">
        {/* Bouton Retour */}
        <div style={{ marginBottom: 24 }}>
          <button onClick={() => navigate('/')} data-testid="back-btn"
            style={{ fontFamily: serif, fontSize: 14, padding: '10px 20px', borderRadius: 8, cursor: 'pointer', color: '#D4AF37', backgroundColor: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.3)', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500, transition: 'all 0.3s' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(212,175,55,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(212,175,55,0.08)'; }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            Retour
          </button>
        </div>

        <div className="text-center" style={{ marginBottom: 32 }}>
          <h1 data-testid="pricing-title" style={{ fontFamily: serif, fontSize: 32, color: '#D4AF37', fontWeight: 400, letterSpacing: 1 }}>Nos Offres</h1>
          <p style={{ fontFamily: serif, fontSize: 14, color: '#FAF3E0', fontStyle: 'italic', marginTop: 8 }}>
            Choisissez l'ecrin qui protegera votre histoire
          </p>
          {/* Trial offer banner */}
          <div data-testid="trial-offer-banner" style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            padding: '10px 22px', borderRadius: 24, marginTop: 16,
            background: 'linear-gradient(135deg, rgba(212,175,55,0.08), rgba(212,175,55,0.02))',
            border: '1px solid rgba(212,175,55,0.2)',
          }}>
            <img src="/bobine-reel-transparent.png" alt="" style={{ width: 18, height: 18, objectFit: 'contain', filter: 'drop-shadow(0 0 4px rgba(212,175,55,0.4))' }} />
            <span style={{ fontFamily: serif, fontSize: 13, color: '#D4AF37', fontWeight: 500, letterSpacing: 0.3 }}>
              Decouvrez l'ecrin Bobine : 30 jours de serenite offerts
            </span>
          </div>
          {/* Sans engagement */}
          <p data-testid="sans-engagement" style={{
            fontFamily: serif, fontSize: 12, color: '#D4AF37', marginTop: 10,
            letterSpacing: 0.8, fontWeight: 500,
          }}>
            Sans engagement — Resiliez a tout moment
          </p>
        </div>

      {/* ABONNEMENTS */}
      <div style={{ marginBottom: 40 }}>
        <h2 data-testid="subscriptions-title" style={{ fontFamily: serif, fontSize: 18, color: '#D4AF37', textAlign: 'center', marginBottom: 16, fontWeight: 400, letterSpacing: 2, textTransform: 'uppercase' }}>
          Abonnements
        </h2>

        {/* Toggle Mensuel / Annuel */}
        <div data-testid="billing-toggle" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 24 }}>
          <span style={{ fontFamily: serif, fontSize: 13, color: billingAnnual ? '#FAF3E0' : '#D4AF37', fontWeight: billingAnnual ? 400 : 600, transition: 'all 0.3s' }}>
            Mensuel
          </span>
          <button
            onClick={() => setBillingAnnual(!billingAnnual)}
            data-testid="billing-toggle-btn"
            style={{
              position: 'relative', width: 48, height: 26, borderRadius: 13, cursor: 'pointer', border: 'none',
              backgroundColor: billingAnnual ? '#D4AF37' : 'rgba(212,175,55,0.2)', transition: 'background-color 0.3s', padding: 0,
            }}
          >
            <div style={{
              position: 'absolute', top: 3, left: billingAnnual ? 25 : 3,
              width: 20, height: 20, borderRadius: '50%',
              backgroundColor: billingAnnual ? '#000' : '#D4AF37',
              transition: 'all 0.3s',
            }} />
          </button>
          <span style={{ fontFamily: serif, fontSize: 13, color: billingAnnual ? '#D4AF37' : '#FAF3E0', fontWeight: billingAnnual ? 600 : 400, transition: 'all 0.3s' }}>
            Annuel
          </span>
          {billingAnnual && (
            <span data-testid="annual-badge" style={{
              fontFamily: serif, fontSize: 10, fontWeight: 600, letterSpacing: 1,
              backgroundColor: 'rgba(212,175,55,0.15)', color: '#D4AF37',
              border: '1px solid rgba(212,175,55,0.3)',
              padding: '3px 10px', borderRadius: 20,
            }}>
              -20%
            </span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
          {plans.filter(p => p.plan_id === 'eveil' || p.plan_id === 'heritage').map(plan => (
            <PlanCard key={plan.plan_id} plan={plan} isCurrent={userPlan === plan.plan_id} popular={plan.plan_id === 'heritage'} />
          ))}
        </div>
      </div>

      {/* PACKS LIBERTÉ */}
      <div style={{ borderTop: '1px solid rgba(212,175,55,0.15)', paddingTop: 40, marginBottom: 24 }}>
        <h2 data-testid="liberte-title" style={{ fontFamily: serif, fontSize: 18, color: '#D4AF37', textAlign: 'center', marginBottom: 8, fontWeight: 400, letterSpacing: 2, textTransform: 'uppercase' }}>
          Packs Liberte
        </h2>
        <p style={{ fontFamily: serif, fontSize: 13, color: '#FAF3E0', textAlign: 'center', fontStyle: 'italic' }}>
          Achat unique, sans engagement — {libertePacks[0]?.free_restorations || 5} restaurations offertes par pack
        </p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 40 }}>
        {libertePacks.map(pack => <LiberteCard key={pack.pack_id} pack={pack} />)}
      </div>

      </main>
    </div>
  );
};

export default PricingPage;
